import {
  Action,
  combineReducers,
  createFeatureSelector,
  createSelector,
} from '@ngrx/store';
import * as fromStandingAuthCheck from './sa-check.reducers';
import * as fromStandingAuthWire from './sa-wire.reducer';
import * as fromStandingAuthAch from './sa-ach.reducer';
import * as fromCreateStandingAuthForm from './standing-authorizations-form.reducer';
import * as fromCreateStandingAuthInternalTransfersInfo from './sa-internal-transfer.reducer';
import * as fromTwePopupInfo from './sa-twe-popup.reducers';
import * as fromTweTableState from './sa-twe-table-state.reducer';
import * as fromStagedStandingAuthForm from './staged-standing-authorizations-form.reducer';
import * as fromMasterLetters from './master-letters-reducers';
import * as fromMasterStandingAuthTableState from './sa-master-status-table-state.reducer'
import { CheckStandingAuthDetails, ItStandingAuthDetails, StandingAuthDetails, StandingAuthorizationForm, WireStandingAuthDetails, WireStandingAuthForm, isAchExtraPrompts, isAchFormSA, isAchStandingAuthDetails } from '../models/standingAuthorizationForm';
import { Constants, RegTypeCode, transactionDirection, transactionTypes } from '../../shared/utilities/Constants';
import { EAuthModel } from '../../create-request/models/internal-transfer/EAuthModel';
import { getFilterDate } from '../../create-request/components/transactions-table/table-helpers';
import { TaxWithholdingFields } from '../../create-request/models/form/TransactionForm';
import { selectEAuthInfo } from '../../shared/reducers';
import { selectCreateStandingAuthEAuthClients } from '../../setup-eAuthorization/reducers';
import { initialStandingAuthCheckInfoState } from './sa-check.reducers';

export const standingAuthFeatureKey = 'standingAuthorization';

export interface standingAuthorizationState {
  [fromCreateStandingAuthForm.standingAuthFormKey]: fromCreateStandingAuthForm.State;
  [fromCreateStandingAuthInternalTransfersInfo.standingAuthitInfoKey]: fromCreateStandingAuthInternalTransfersInfo.State;
  [fromStandingAuthCheck.standingAuthCheckInfoKey]: fromStandingAuthCheck.State;
  [fromStandingAuthWire.standingAuthWireInfoKey]: fromStandingAuthWire.State;
  [fromStandingAuthAch.standingAuthAchInfoKey]: fromStandingAuthAch.State;
  [fromTwePopupInfo.twePopupKey]: fromTwePopupInfo.State;
  [fromTweTableState.tweTableStateKey]: fromTweTableState.State;
  [fromStagedStandingAuthForm.key]: fromStagedStandingAuthForm.State;
  [fromMasterLetters.masterLettersKey]: fromMasterLetters.MasterLettersState;
  [fromMasterStandingAuthTableState.saMasterStatusTableStatekey]: fromMasterStandingAuthTableState.State
}

export function reducers(
  state: standingAuthorizationState | undefined,
  action: Action
) {
  return combineReducers({
    [fromCreateStandingAuthForm.standingAuthFormKey]:
      fromCreateStandingAuthForm.standingAuthorizationReducer,
    [fromCreateStandingAuthInternalTransfersInfo.standingAuthitInfoKey]:
      fromCreateStandingAuthInternalTransfersInfo.standingAuthInternalTransferInfoReducer,
    [fromStandingAuthCheck.standingAuthCheckInfoKey]:
      fromStandingAuthCheck.standingAuthCheckReducer,
    [fromStandingAuthWire.standingAuthWireInfoKey]:
      fromStandingAuthWire.standingAuthWireReducer,
    [fromTwePopupInfo.twePopupKey]:
      fromTwePopupInfo.reducer,
    [fromTweTableState.tweTableStateKey]:
      fromTweTableState.tweTableStateReducer,
    [fromStagedStandingAuthForm.key]:
      fromStagedStandingAuthForm.reducer,
    [fromStandingAuthAch.standingAuthAchInfoKey]:
      fromStandingAuthAch.standingAuthAchReducer,
    [fromMasterLetters.masterLettersKey]:
      fromMasterLetters.masterLettersReducer,
    [fromMasterStandingAuthTableState.saMasterStatusTableStatekey]:
      fromMasterStandingAuthTableState.masterStandingAuthTableStateReducer,
  })(state, action);
}

export const selectStandingAuthorization =
  createFeatureSelector<standingAuthorizationState>(standingAuthFeatureKey);

export const selectStandingAuthForm = createSelector(
  selectStandingAuthorization,
  (state) => {
    return state.standingAuthorizationForm;
  }
);

export const selectTwePopupInfo = createSelector(
  selectStandingAuthorization,
  (state) => {
    return state.twePopupInfo;
  }
);

export const selectStagedStandingAuthForm = createSelector(
  selectStandingAuthorization,
  (state) => {
    return state.stagedStandingAuthorizationForm;
  }
);

export const selectTweTableState = createSelector(
  selectStandingAuthorization,
  (state) => {
    return state.tweTableState;
  }
);

export const selectMasterLetters = createSelector(
  selectStandingAuthorization,
  (state) => {
    return state.masterLetters;
  }
);

export const selectMasterLettersData = createSelector(
  selectMasterLetters,
  (state) => {
    return state.letters;
  }
);

export const selectMasterSABookmarkedFilterStatus = createSelector(
  selectMasterLetters,
  (state) => {
    return state.isBookmarkFilterOn;
  }
);

export const selectMasterStandingAuthTableState = createSelector(
  selectStandingAuthorization,
  (state) => {
    return state.saMasterStatusTableState
  }
)

export const selectIsSubmitandCreateBundledSa = createSelector(
  selectStandingAuthForm,
  (state) => {
    return state.isSubmitAndCreateBundledSa ?? false;
  }
);

export const selectStandingAuthFormStandingAuthDetails = createSelector(
  selectStandingAuthForm,
  (state) => {
    return state.standingAuthorizationDetails;
  }
);

export const selectStagedStandingAuthFormStandingAuthDetails = createSelector(
  selectStagedStandingAuthForm,
  (state) => {
    return state.standingAuthorizationDetails;
  }
);

export const selectAchRecurringFrequency = createSelector(
  selectStagedStandingAuthForm,
  (state) => {
    if (isAchFormSA(state)) {
      return state.standingAuthorizationDetails.recurringFrequency;
    }
    return '';
  }
);

export const selectStandingAuthFormExtraPrompts = createSelector(
  selectStandingAuthForm,
  (state) => {
    return state?.extraPrompts;
  }
);

export const selectStagedStandingAuthFormExtraPrompts = createSelector(
  selectStagedStandingAuthForm,
  (state) => {
    return state?.extraPrompts;
  }
);

export const selectStandingAuthFormDirection = createSelector(
  selectStandingAuthFormExtraPrompts,
  (state) => {
    if (state && isAchExtraPrompts(state)) {
      return state.transferDirection;
    }
    return '';
  }
)

export const selectStagedStandingAuthFormDirection = createSelector(
  selectStagedStandingAuthFormExtraPrompts,
  (state) => {
    if (state && isAchExtraPrompts(state)) {
      return state.transferDirection;
    }
    return '';
  }
)

export const selectStandingAuthIdSA = createSelector(
  selectStandingAuthFormExtraPrompts,
  (state) => {
    if (state && isAchExtraPrompts(state)) {
      return state.standingAuthId || '';
    }
    return '';
  }
);

export const selectStagedStandingAuthIdSA = createSelector(
  selectStagedStandingAuthFormExtraPrompts,
  (state) => {
    if (state && isAchExtraPrompts(state)) {
      return state.standingAuthId || '';
    }
    return '';
  }
);

export const selectTransactionType = createSelector(
  selectStandingAuthForm,
  (state) => {
    return state.transactionType;
  }
);

export const selectCreateStandingAuthInProgress = createSelector(
  selectStandingAuthForm,
  (state) => {
    return state.createStandingAuthInProgress;
  }
);

export const selectStandingAuthInternalTransferInfo = createSelector(selectStandingAuthorization, (state) => {
  return state.internalTransferInfo;
});

export const selectInternalTransferInfoIraFieldsSA = createSelector(selectStandingAuthInternalTransferInfo, (state) => {
  return state.iraFields;
})



export const selectToAccountInfo = createSelector(selectStandingAuthInternalTransferInfo, (state) => {
  return state.toAccountInfo;
})

export const selectToAccountRegistrationType = createSelector(selectToAccountInfo, (state) => {
  return state?.registrationType;
});

export const selectisStandingAuthI401KToAccount = createSelector(selectToAccountRegistrationType, (state) => {
  return state === RegTypeCode.INDIVIDUAL_401K || state === RegTypeCode.ROTH_401K;
});

export const selectToAccountInfoErrorText = createSelector(selectStandingAuthInternalTransferInfo, (state) => {
  return state.loadToAccountErrorText;
})

export const selectToAccountState = createSelector(selectStandingAuthInternalTransferInfo, (state) => {
  return state.toAccountState;
})
export const selectStandingAuthFormServiceError = createSelector(
  selectStandingAuthForm,
  (state) => {
    return state.serviceError;
  }
);
export const selectStandingAuthWireInfo = createSelector(
  selectStandingAuthorization,
  (state) => {
    return state?.wireInfo;
  }
);

export const selectABAStandingAuth = createSelector(
  selectStandingAuthFormStandingAuthDetails,
  (state) => {
    if (isWireStandingAuthDetails(state)){
    return state.aba;
    }
  }
);


export const selectStandingAuthAchInfo = createSelector(
  selectStandingAuthorization,
  (state) => {
    return state.achInfo;
  }
);

export const selectMoneyLinkType = createSelector(selectStandingAuthAchInfo, (state) => {
  return state.moneyLinkType
})

export const selectAchInfoToFromAccountSA = createSelector(selectStandingAuthAchInfo, (state) => {
  return state.toFromAccount
})

export const selectAchProcessDate = createSelector(selectStandingAuthAchInfo, (state) => {
  return state.processDate;
})

export const selectAchDefaultProcessDate = createSelector(selectStandingAuthAchInfo, (state) => {
  return state.defaultProcessDate;
})

export const selectStagedTransactionType = createSelector(
  selectStagedStandingAuthForm,
  (state) => {
    return state.transactionType;
  }
);

export const selectStandingAuthWireIntermediarySelection = createSelector(
  selectStandingAuthFormStandingAuthDetails,
  (state) => {
    if (state && isWireStandingAuthDetails(state)) {
      return state.intermediarySelection || '';
    }
    return '';
  }
);

export const selectReceivingAccountHolder = createSelector(
  selectStandingAuthWireInfo,
  (state) => {
    return state?.receivingAccountHolder;
  }
);

export const selectReceivingAccountHolderIso = createSelector(
  selectStandingAuthWireInfo,
  (state) => {
    return state?.receivingAccountHolderIso;
  }
);

export const selectTaxRemittance = createSelector(
  selectStandingAuthWireInfo,
  (state) => {
    return state?.taxRemittance;
  }
);

export const selectWireInputV3CallFinished = createSelector(
  selectStandingAuthWireInfo,
  (state) => {
    return state?.inputCallV3Finished;
  }
);

export const selectAchInquiryCallFinished = createSelector(
  selectStandingAuthAchInfo,
  (state) => {
    return state.inquiryCallFinished;
  }
);

export const selectAchInquiryCallFailed = createSelector(
  selectStandingAuthAchInfo,
  (state) => {
    return state.inquiryCallFinished;
  }
);

export const selectStandingAuthCallSucceededAch = createSelector(
  selectStandingAuthAchInfo,
  (state) => {
    return state.standingAuthorizationsCallSucceeded;
  }
)

export const selectAchVerifyCallFinished = createSelector(selectStandingAuthAchInfo, (state) => {
  return state.verifyCallFinished;
});

export const selectWireCountries = createSelector(
  selectStandingAuthWireInfo,
  (state) => {
    return state?.countryList;
  }
);

export const selectCompletionDate = createSelector(
  selectStandingAuthForm,
  (state) => {
    if (isAchFormSA(state)) {
      return state.standingAuthorizationDetails.completionDate;
    }
    return '';
  }
);

export const selectSettlementDate = createSelector(
  selectStandingAuthForm,
  selectStandingAuthFormDirection,
  (state, direction) => {
    if (isAchFormSA(state) && direction === transactionDirection.in.toLowerCase()) {
      return state.standingAuthorizationDetails.settlementDate;
    }
    return '';
  }
);

export const selectFundsAvailableToTrade = createSelector(
  selectStandingAuthForm,
  selectStandingAuthFormDirection,
  (state, direction) => {
    if (isAchFormSA(state) && direction === transactionDirection.in.toLowerCase()) {
      return state.standingAuthorizationDetails.fundsAvailableToTrade;
    }
    return '';
  }
);

export const selectWithdrawalDate = createSelector(
  selectStandingAuthForm,
  (state) => {
    if (isAchFormSA(state)) {
      return state.standingAuthorizationDetails.withdrawalDate;
    }
    return '';
  }
);

export const selectTaxWithholding = createSelector(selectStandingAuthAchInfo, (state) => {
  const taxWithholding: TaxWithholdingFields = {
    fedTaxPercent: state.fedTaxPercent,
    fedTaxAmount: state.fedTaxAmount,
    stateTaxPercent: state.stateTaxPercent,
    stateTaxAmount: state.stateTaxAmount,
    distributionReason: state.distributionReason,
    distributionReasonMap: state.distributionReasonMap,
    stateHasTaxes: state.withholdStateTax
  };
  return taxWithholding;
});

export const selectMaxTransferAmount = createSelector(
  selectStandingAuthAchInfo,
  (state) => state ? state.maxTransferAmount : ''
)

export const selectAchAmountType = createSelector(selectStandingAuthAchInfo, (state) => {
  return state.amountType;
});

export const selectACHAvailableCashMMF = createSelector(
  selectStandingAuthAchInfo,
  (state) => {
    return state.availableCashMMF
  }
)

export const selectACHAvailableCashMargin = createSelector(
  selectStandingAuthAchInfo,
  (state) => {
    return state.availableCashMargin
  }
)

export const selectContributionOptions = createSelector(
  selectStandingAuthAchInfo,
  (state) => state ? state.contributionOptions : []
)

export const selectContributionReason = createSelector(
  selectStandingAuthAchInfo,
  (state) => {
    return state.contributionReason
  }
)

export const selectFrequency = createSelector(
  selectStandingAuthAchInfo,
  (state) => {
    return state.frequency
  }
)

export const selectCanDisplayCreditToInfo = createSelector(selectStandingAuthWireInfo, (state) => {
  return state?.canDisplayCreditToInfo;
})

export const missingThirdPartyAddress = createSelector(selectStandingAuthWireInfo, (state) => {
  return state?.missingThirdPartyAddress;
});

export const selectWireFedTaxPercent = createSelector(selectStandingAuthWireInfo, (state) => {
  return state?.fedTaxPercent;
});

export const selectWireStateTaxPercent = createSelector(selectStandingAuthWireInfo, (state) => {
  return state?.stateTaxPercent;
});

export const selectWireStateTypeIndicator = createSelector(selectStandingAuthWireInfo, (state) => {
  return state?.stateTypeIndicator;
});

export const selectWireStateCode = createSelector(selectStandingAuthWireInfo, (state) => {
  return state?.stateCd;
});

export const selectWireStateHasTaxes = createSelector(selectStandingAuthWireInfo, (state) => {
  return state?.stateHasTaxes;
});

export const selectAchDefaultFedTaxPercent = createSelector(selectStandingAuthAchInfo, (state) => {
  return state.defaultFedTaxPercent;
});

export const selectAchDefaultStateTaxPercent = createSelector(selectStandingAuthAchInfo, (state) => {
  return state.defaultStateTaxPercent;
});

export const selectAchStateTypeIndicator = createSelector(selectStandingAuthAchInfo, (state) => {
  return state.stateTypeIndicator;
});

export const selectAchStateCode = createSelector(selectStandingAuthAchInfo, (state) => {
  return state.stateCd;
});

export const selectAchCanDisplayStateTax = createSelector(selectStandingAuthAchInfo, (state) => {
  return state.canDisplayStateTax;
});

export const selectAchDistributionReasonsList = createSelector(selectStandingAuthAchInfo, (state) => {
  return state.distributionReasonMap;
});

export const selectAchDistributionReason = createSelector(selectStandingAuthFormStandingAuthDetails, (state) => {
  if (state && isAchStandingAuthDetails(state)) {
    return state.taxWithholdingsFormGroup?.distributionReason || '';
  }
  return '';
});

export const selectStandingAuthAchInputAmount = createSelector(selectStandingAuthFormStandingAuthDetails, (state) => {
  if (state && isAchStandingAuthDetails(state)) {
    return state.amount;
  }
});

export const selectStandingAuthAchInputAmountType = createSelector(selectStandingAuthFormStandingAuthDetails, (state) => {
  if (state && isAchStandingAuthDetails(state)) {
    return state.amountType;
  }
});

export const selectSaModifiedPayeeCheckbox = createSelector(
  selectStandingAuthFormStandingAuthDetails,
  (state) => {
    if (isCheckStandingAuthDetails(state)) {
      return state.modifiedPayeeCheckbox;
    }
    return false;
  }
);

export const selectStandingAuthCheckInfo = createSelector(selectStandingAuthorization, (state) => {
  return state.checkInfo;
});

export const selectStandingAuthCheckIsRmdEligible = createSelector(
  selectStandingAuthCheckInfo,
  (state) => {
    return state.isRmdEligible;
  }
);

export const selectStandingAuthPendForRelease = createSelector(
  selectStagedTransactionType,
  selectStandingAuthWireInfo,
  selectStandingAuthCheckInfo,
  selectStandingAuthInternalTransferInfo,
    (type, wireInfo, checkInfo, journalInfo) => {
    switch (type) {
      case transactionTypes.wire:
        return wireInfo.authResponse?.pendForRelease;
      case transactionTypes.check:
        return checkInfo.authResponse?.pendForRelease;
      case transactionTypes.internalTransfer:
        return journalInfo.journalAuthResponse?.pendForRelease;
      default:
        return false;
    }
  }
);

export const selectStandingAuthWireCustomRuleSplitPendForRelease = createSelector(
  selectStandingAuthWireInfo,
  (wireInfo) => {
    return wireInfo?.authResponse?.pendForRelease ?? false;
  }
);

export const selectStandingAuthWireCustomRuleSplitCallHasError = createSelector(
  selectStandingAuthWireInfo,
  (wireInfo) => {
    return wireInfo?.hasError ?? false;
  }
);

export const selectStandingAuthInternalTransferCustomRuleSplitCallHasError = createSelector(
  selectStandingAuthInternalTransferInfo,
  selectStandingAuthForm,
  (internalTransferInfo, form) => {
    return (internalTransferInfo?.verifyCallHasError || form?.serviceError?.hasError) ?? false;
  }
);

export const selectStandingAuthItCustomRuleSplitPendForRelease = createSelector(
  selectStandingAuthInternalTransferInfo,
  (internalTransferInfo) => {
    return internalTransferInfo?.journalAuthResponse?.pendForRelease ?? false;
  }
);

// checks if any standing auth rules were matched during rule evaluation from InR API
// empty pendForReleaseReason indicates no rule was matched
export const selectStandingAuthPendForReleaseCustomRule = createSelector(
  selectStagedTransactionType,
  selectStandingAuthWireInfo,
  selectStandingAuthCheckInfo,
  selectStandingAuthInternalTransferInfo,
  (type, wireInfo, checkInfo, internalTransferInfo) => {
    switch (type) {
      case transactionTypes.wire:
        return wireInfo.authResponse?.pendForReleaseReason === '';
      case transactionTypes.check:
        return checkInfo.authResponse?.pendForReleaseReason === '';
      case transactionTypes.internalTransfer:
        return internalTransferInfo.journalAuthResponse?.pendForReleaseReason === '';
      default:
        return false;
    }
  }
);

export const selectStandingAuthCanRelease = createSelector(
  selectStagedTransactionType,
  selectStandingAuthWireInfo,
  selectStandingAuthCheckInfo,
  selectStandingAuthInternalTransferInfo,
  (type, wireInfo, checkInfo, journalInfo) => {
    switch (type) {
      case transactionTypes.wire:
        return wireInfo.authResponse?.canRelease;
      case transactionTypes.check:
        return checkInfo.authResponse?.canRelease;
      case transactionTypes.internalTransfer:
        return journalInfo.journalAuthResponse?.canRelease;
      default:
        return false;
    }
  }
);

export const selectCheckReasonMap = createSelector(selectStandingAuthCheckInfo, (state) => {
  return state.checkReasonMap;
});

export const selectUseConstructedPayee = createSelector(selectStandingAuthCheckInfo, (state) => state.useConstructedPayee);

export const selectPayeeType = createSelector(selectStandingAuthFormStandingAuthDetails, (state) => {
  if (state && isCheckStandingAuthDetails(state)) {
    return state.payeeType || '';
  }
  return '';
});

export const selectPayeeName = createSelector(selectStandingAuthFormStandingAuthDetails, (state) => {
  if (state && isWireStandingAuthDetails(state)) {
    if (state.recipient === 'thirdParty') {
      return state.name;
    }
    else {
      return state.payeeName;
    }
  }
  else {
    return '';
  }
})

export const selectModifiedPayeeCheckbox = createSelector(
  selectStandingAuthFormStandingAuthDetails,
  (state) => {
    return isWireStandingAuthDetails(state) ? state.modifiedPayeeCheckbox : false;
  }
);

export const selectWireModifiedPayeeName1 = createSelector(selectStandingAuthFormStandingAuthDetails, (state) => {
  if (isWireStandingAuthDetails(state)) {
    return state.modifiedPayeeName1;
  }
})

export const selectWireModifiedPayeeName2 = createSelector(selectStandingAuthFormStandingAuthDetails, (state) => {
  if (isWireStandingAuthDetails(state)) {
    return state.modifiedPayeeName2;
  }
})

export const selectWireIsoModifiedPayeeName = createSelector(selectStandingAuthFormStandingAuthDetails, (state) => {
  if (isWireStandingAuthDetails(state)) {
    return state.isoModifiedPayeeName;
  }
})

export const selectCheckDistributionReason = createSelector(selectStandingAuthFormStandingAuthDetails, (state) => {
  if (state && isCheckStandingAuthDetails(state)) {
    return state.distributionReason || '';
  }
  return '';
});

export const isCheckStandingAuthDetails = (details: StandingAuthDetails): details is CheckStandingAuthDetails => {
  if (details && 'payeeType' in details) {
    return true;
  }
  return false;
}

export const selectDefaultPayee = createSelector(selectStandingAuthCheckInfo, (state) => {
  return state.defaultPayee;
});

export const selectCheckRecipientSelection = createSelector(selectStandingAuthFormStandingAuthDetails, (state) => {
  if (state && isCheckStandingAuthDetails(state)) {
    return state.recipient || '';
  }
  return '';
});

export const isWireStandingAuthDetails = (details: StandingAuthDetails): details is WireStandingAuthDetails => {
  if (details && 'recipient' in details) {
    return true;
  }
  return false;
}

export const isWireForm = (form: StandingAuthorizationForm): form is WireStandingAuthForm => {
  return form.transactionType === transactionTypes.wire;
}

export const selectWireRecipientSelection = createSelector(selectStandingAuthFormStandingAuthDetails, (state) => {
  if (state && isWireStandingAuthDetails(state)) {
    return state.recipient || '';
  }
  return '';
});

export const selectStateList = createSelector(selectStandingAuthCheckInfo, (state) => {
  return state.stateList;
});

export const selectCheckCountries = createSelector(selectStandingAuthCheckInfo, (state) => {
  return state.countryList;
});

export const selectCheckPayee = createSelector(selectStandingAuthCheckInfo, (state) => {
  return state.payee;
});

export const selectCheckDefaultRecipient = createSelector(selectStandingAuthCheckInfo, (state) => {
  return state.defaultRecipient.length > 0 ? state.defaultRecipient : [];
});

export const selectCheckInputCallFinished = createSelector(selectStandingAuthCheckInfo, (state) => {
  return state.inputCallFinished;
})

export const selectCheckInquiryCallFinished = createSelector(selectStandingAuthCheckInfo, (state) => {
  return state.inquiryCallFinished;
})

export const selectCheckInputCallSucceeded = createSelector(selectStandingAuthCheckInfo, (state) => {
  return state.inputCallSucceeded;
})

export const selectCheckVerifyCallFinished = createSelector(selectStandingAuthCheckInfo, (state) => {
  return state.verifyCallFinished;
});

export const selectCheckVerifyCallError = createSelector(selectStandingAuthCheckInfo, (state) => {
  return state.serviceError;
})

export const selectWireAbaCode = createSelector(selectStandingAuthWireInfo, (state) => {
  return state?.abaStatusCode;
});

export const selectCheckFedTaxPercent = createSelector(selectStandingAuthCheckInfo, (state) => {
  return state.defaultFedTaxRate;
});

export const selectCheckStateTaxPercent = createSelector(selectStandingAuthCheckInfo, (state) => {
  return state.defaultStateTaxRate;
});

export const selectCheckStateTypeIndicator = createSelector(selectStandingAuthCheckInfo, (state) => {
  return state.stateTypeIndicator;
});

export const selectCheckStateCode = createSelector(selectStandingAuthCheckInfo, (state) => {
  return state.stateCd;
});

export const selectCheckShouldShowStateTax = createSelector(selectStandingAuthCheckInfo, (state) => {
  return state.shouldShowStateTax;
});

export const selectCheckDistributionReasonsList = createSelector(selectStandingAuthCheckInfo, (state) => {
  return state.distributionReasonsList;
});

export const selectStandingAuthCashieringIraAgreement = createSelector(selectStandingAuthCheckInfo, selectStandingAuthAchInfo,
  (checkInfo, achInfo) => {
    return achInfo?.cashieringIraAgreementText || checkInfo?.cashieringIraAgreementText;
});

export const selectCheckPendingForCTWE = createSelector(selectStandingAuthCheckInfo, (state) => {
  return state.isPendingForCTWE;
});

export const selectWireDetailsErrorStandingAuth = createSelector(selectStandingAuthWireInfo, (state) => {
  return state.abaMessageFields?.errorList[0]?.messageText;
});

export const selectWireDetailsWarningStandingAuth = createSelector(selectStandingAuthWireInfo, (state) => {
  return state.abaMessageFields?.warningList[0]?.messageText;
});

export const isItStandingAuthDetails = (details: StandingAuthDetails): details is ItStandingAuthDetails => {
  if (details && 'toAccount' in details) {
    return true;
  }
  return false;
}

export const selectAchAcknowledgeCallError = createSelector(selectStandingAuthAchInfo, (state) => {
  return state.messageFields?.errorList[0]?.messageText;
})

export const selectAchVerifyCallError = createSelector(selectStandingAuthAchInfo, (state) => {
  return state.messageFields?.errorList[0]?.messageText;
})

export const selectAchVerifyCallWarning = createSelector(selectStandingAuthAchInfo, (state) => {
  return state.messageFields?.warningList;
})

export const selectInternalTransferVerifyCallError = createSelector(selectStandingAuthInternalTransferInfo, (state) => {
  return state.verifyCallErrorText;
})

export const selectInternalTransfersVerifyCallFinished = createSelector(selectStandingAuthInternalTransferInfo, (state) => {
  return state.verifyCallFinished;
})

export const selectWireVerifyCallFinished = createSelector(selectStandingAuthWireInfo, (state) => {
  return state.verifyV3CallFinished;
})

export const selectInternalTransferInputCallFinished = createSelector(selectStandingAuthInternalTransferInfo, (state) => {
  return state.inputCallFinished;
})

export const selectInternalTransferEAuthModel = createSelector(selectStandingAuthInternalTransferInfo, (state) => {
  return state.eAuthModel;
})

export const selectInternalTransferFedTaxPercent = createSelector(selectStandingAuthInternalTransferInfo, (state) => {
  return state.iraFields.fedTaxPercent;
});

export const selectInternalTransferStateTaxPercent = createSelector(selectStandingAuthInternalTransferInfo, (state) => {
  return state.iraFields.stateTaxPercent;
});

export const selectInternalTransferStateTypeIndicator = createSelector(selectStandingAuthInternalTransferInfo, (state) => {
  return state.iraFields.stateTypeIndicator;
});

export const selectInternalTransferStateCode = createSelector(selectStandingAuthInternalTransferInfo, (state) => {
  return state.stateCd;
});

export const selectInternalTransferStateHasTaxes = createSelector(selectStandingAuthInternalTransferInfo, (state) => {
  return state.shouldShowStateTax;
});

export const selectInternalTransferPendingForCTWE = createSelector(selectStandingAuthInternalTransferInfo, (state) => {
  return state.isPendingOnlyForCTWE;
});

export const selectWarningList = createSelector(
  selectStandingAuthInternalTransferInfo,
  (itInfo) => {
    return itInfo.messageInfo?.warningList;
  });

export const selectEAuthModel = createSelector(
  selectStandingAuthInternalTransferInfo,
  selectStandingAuthCheckInfo,
  selectStandingAuthWireInfo,
  selectTransactionType,
  (itInfo, checkInfo, wireInfo, transactionType) => {
    switch (transactionType) {
      case transactionTypes.internalTransfer:
        return itInfo.eAuthModel;
      case transactionTypes.check:
        return checkInfo.eAuthModel;
      case transactionTypes.wire:
        return wireInfo.eAuthModel;
      default:
        return new EAuthModel();
    }
  }
);

export const selectShowClientAuthorization = createSelector(
  selectStandingAuthCheckInfo,
  selectStandingAuthInternalTransferInfo,
  selectStandingAuthWireInfo,
  selectTransactionType,
  (checkInfo, itInfo, wireInfo, transactionType) => {
    switch (transactionType) {
      case transactionTypes.internalTransfer:
        return itInfo.clientAuthorizationNeeded && itInfo.eauthAccess;
      case transactionTypes.check:
        return checkInfo.displayClientAuthorizationSection && !checkInfo.isPendingOnlyForCTWE;
      case transactionTypes.wire:
        return wireInfo.clientAuthorizationNeeded && wireInfo.eauthAccess;
      default:
        return false;
    }
  }
);

export const selectClientAuthorizationNeeded = createSelector(
  selectStandingAuthInternalTransferInfo,
  selectStandingAuthCheckInfo,
  selectStandingAuthWireInfo,
  selectTransactionType,
  (itInfo, checkInfo, wireInfo, transactionType) => {
    switch (transactionType) {
      case transactionTypes.internalTransfer:
        return itInfo.clientAuthorizationNeeded;
      case transactionTypes.check:
        return checkInfo.displayClientAuthorizationSection;
      case transactionTypes.wire:
        return wireInfo.clientAuthorizationNeeded;
      default:
        return false;
    }
  });

export const selectSaPendReasons = createSelector(
  selectStandingAuthWireInfo,
  selectStandingAuthInternalTransferInfo,
  selectStandingAuthCheckInfo,
  (wireInfo, itInfo, checkInfo) => {
    return [
      ...(itInfo?.pendReasons ?? []),
      ...(wireInfo?.pendReasons ?? []),
      ...(checkInfo?.pendReasons ?? [])
    ]
  });

export const selectIsSaPending = createSelector(
  selectSaPendReasons,
  (pendReasons) => {
    return pendReasons.length > 0;
  }
);

export const selectIsSaPendingAuthorization = createSelector(
  selectStagedTransactionType,
  selectStandingAuthInternalTransferInfo,
  selectStandingAuthWireInfo,
  selectStandingAuthCheckInfo,
  (type, itInfo, wireInfo, checkInfo) => {
    switch (type) {
      case transactionTypes.internalTransfer:
        return itInfo.clientAuthorizationNeeded;
      case transactionTypes.wire:
        return wireInfo.clientAuthorizationNeeded;
      case transactionTypes.check:
        return checkInfo.clientAuthorizationNeeded;
      default:
        return false;
    }
  });

export const selectEAuthAccountHolderSelected = createSelector(selectEAuthInfo, (state) => {
  return state.isAccountHolderSelected;
});

export const selectEAuthSelectedAccountHolderIds = createSelector(selectEAuthInfo, (state) => {
  return state.selectedAccountHolder;
});

export const selectSAMappedPendReason = createSelector(
  selectSaPendReasons,
  selectEAuthAccountHolderSelected,
  (reasons, eAuthSelected) => {
    if (reasons.includes('Client Authorization Needed (LOA)')) {
      return Constants.saLoaNeeded;
    } else if (reasons.includes('Electronic Authorization Needed')) {
      return Constants.saEauthNeeded;
    } else if (reasons.includes('Client Authorization Needed For Tax Elections') || reasons.includes('Schwab is Reviewing for Client Auth due to NDFA/LPFA')) {
      return eAuthSelected ? Constants.saEauthNeeded : Constants.saLoaNeeded;
    }
    return Constants.defaultSaPendDescription;
  }
);

export const selectSLOAVerifyCallFinished = createSelector(
  selectWireVerifyCallFinished,
  selectInternalTransfersVerifyCallFinished,
  selectCheckVerifyCallFinished,
  selectAchVerifyCallFinished,
  (wireVerifyFinished, internalTransferVerifyFinished, checkVerifyCallFinished, achVerifyCallFinished) => {
    return wireVerifyFinished || internalTransferVerifyFinished || checkVerifyCallFinished || achVerifyCallFinished;
  }
);

export const selectTweCashieringTransactions = createSelector(selectTwePopupInfo, (state) => {
  return state.accountDetails.cashieringRequestAprs;
});

export const selectTweRecurringTransactions = createSelector(selectTwePopupInfo, (state) => {
  return state.accountDetails.recurringRequests;
});

export const selectTweStandingAuth = createSelector(selectTwePopupInfo, (state) => {
  return state.accountDetails.sloaRequests;
});

export const selectTweUpcomingTransactions = createSelector(
  selectTweCashieringTransactions, (cashieringTransactions) => {
    return cashieringTransactions.filter((val) => {
      const next15Day = getFilterDate(14)
      const today = getFilterDate()
      const processDate = new Date(val.formattedDate)
      return processDate >= today.onlyDate && processDate <= next15Day.onlyDate
    });
  });

export const selectStandingAuthACHTaxWithholdings = createSelector(selectStandingAuthFormStandingAuthDetails, (state) => {
  if (state && (isWireStandingAuthDetails(state) || isItStandingAuthDetails(state) || isCheckStandingAuthDetails(state))) {
    return {
      withholdFedTax: false,
      withholdStateTax: false,
      fedTaxPercent: state.fedTaxPercent,
      fedTaxAmount: '',
      stateTaxPercent: state.stateTaxPercent,
      stateTaxAmount: '',
      distributionReason: '',
      distributionReasonMap: []
    } as TaxWithholdingFields
  }
  if (state && (isAchStandingAuthDetails(state)))
  {
    return {
      withholdFedTax: false,
      withholdStateTax: false,
      fedTaxPercent: state.taxWithholdingsFormGroup?.fedTaxPercent,
      fedTaxAmount: '',
      stateTaxPercent: state.taxWithholdingsFormGroup?.stateTaxPercent,
      stateTaxAmount: '',
      distributionReason: '',
      distributionReasonMap: []
    } as TaxWithholdingFields
  }
  else {
    return {
      withholdFedTax: false,
      withholdStateTax: false,
      fedTaxPercent: '',
      fedTaxAmount: '',
      stateTaxPercent: '',
      stateTaxAmount: '',
      distributionReason: '',
      distributionReasonMap: []
    } as TaxWithholdingFields
  }
});


export const selectTwePopupIndicator = createSelector(selectTwePopupInfo, (state) => {
  return state.openTwePopup;
});

export const selectOpenTwePopupConditions = createSelector(
  selectSLOAVerifyCallFinished,
  selectTwePopupIndicator,
  (verifyCallFinished, openTwePopup) => {
    return {
      verifyCallFinished,
      openTwePopup
    }
  }
);

export const selectStandingAuthFormHasError = createSelector(
  selectStandingAuthFormServiceError,
  (state) => {
    return state.hasError;
  }
);

export const selectEauthModel = createSelector(
  selectTransactionType,
  selectStandingAuthInternalTransferInfo,
  selectStandingAuthWireInfo,
  selectStandingAuthCheckInfo,
  (type, itInfo, wireInfo, checkInfo) => {
    switch (type) {
      case transactionTypes.internalTransfer:
        return itInfo.eAuthModel;
      case transactionTypes.wire:
        return wireInfo.eAuthModel;
      case transactionTypes.check:
        return checkInfo.eAuthModel;
      default:
        return new EAuthModel();
    }
  }
);

export const selectRevalidationResponse = createSelector(
  selectTransactionType,
  selectStandingAuthCheckInfo,
  selectStandingAuthWireInfo,
  selectStandingAuthInternalTransferInfo,
  (type, checkInfo, wireInfo, itInfo) => {
    switch (type) {
      case transactionTypes.check:
        return checkInfo.revalidation;
      case transactionTypes.wire:
        return wireInfo.revalidation;
      case transactionTypes.internalTransfer:
        return itInfo.revalidation;
      default:
        return initialStandingAuthCheckInfoState.revalidation;
    }
  }
)

export const selectStandingAuthFormPrefilledState = createSelector(selectStandingAuthForm, (state) => {
  return state.prefilledFlow
})

export const selectIsFromEditInProgress = createSelector(selectStandingAuthFormPrefilledState, ({state}) => {
  return state === "inProgress";
});

export const selectIsFromEditStarted = createSelector(selectStandingAuthFormPrefilledState, ({state}) => {
  return state === "started";
});

export const selectIsFromTableOrView = createSelector(selectStandingAuthFormPrefilledState, (state) => {
  return state.name === 'edit-view' || state.name === 'edit-table';
});

export const selectShouldDisableFieldForEditQcd = createSelector(
  selectStandingAuthFormStandingAuthDetails,
  selectIsFromTableOrView,
  (standingAuthDetails, isFromTableOrView) => {
    if (isCheckStandingAuthDetails(standingAuthDetails) && isFromTableOrView) {
      return true;
    }
    return false;
});

export const selectHasEauthAccess = createSelector(
  selectStandingAuthWireInfo,
  selectStandingAuthInternalTransferInfo,
  selectStandingAuthCheckInfo,
  (wireInfo, itInfo, checkInfo) => {
    return wireInfo.eauthAccess || itInfo.eauthAccess || checkInfo.displayClientAuthorizationSection;
  }
)

export const selectAllEauthClientsAwaitingAlliance = createSelector(
  selectEAuthAccountHolderSelected,
  selectEAuthSelectedAccountHolderIds,
  selectCreateStandingAuthEAuthClients,
  selectEauthModel,
  (isAccountHolderSelected, selectedAccountHolders, standingAuthEauthCients, eauthModel) => {
    var clientList = !eauthModel.isOrgAccount ? standingAuthEauthCients.eauthClients :
      isAccountHolderSelected ? standingAuthEauthCients.eauthClients .filter(client => selectedAccountHolders.includes(client.customerId)) : [];
    return clientList.filter(client => client.eauthSetup.status === 'Awaiting Alliance setup');
  }
)

export const selectSaHeaderTaxRates = createSelector(
  selectStagedTransactionType,
  selectStandingAuthAchInfo,
  selectStandingAuthCheckInfo,
  selectStagedStandingAuthFormStandingAuthDetails,
  selectStandingAuthInternalTransferInfo,
  (type, achInfo, checkInfo, formDetails, itInfo) => {
    switch (type) {
      case transactionTypes.ach:
        return { fedTaxPercent: achInfo?.fedTaxPercent ?? '', stateTaxPercent: achInfo?.stateTaxPercent ?? '' };
      case transactionTypes.check:
        return { fedTaxPercent: checkInfo?.fedTaxRate ?? '', stateTaxPercent: (formDetails as CheckStandingAuthDetails)?.stateTaxPercent ?? '' };
      case transactionTypes.wire:
        return { fedTaxPercent: (formDetails as WireStandingAuthDetails)?.fedTaxPercent ?? '', stateTaxPercent: (formDetails as WireStandingAuthDetails)?.stateTaxPercent ?? '' };
      case transactionTypes.internalTransfer:
        return { fedTaxPercent: itInfo?.iraFields?.fedTaxPercent ?? '', stateTaxPercent: itInfo?.iraFields?.stateTaxPercent ?? '' };
      default:
        return { fedTaxPercent: '', stateTaxPercent: '' };
    }
  }
)
